import { Fragment, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  cancelKickoffQueue,
  fetchActiveRuns,
  fetchAwsCost,
  fetchConfigPromotionStats,
  fetchKickoffQueue,
  fetchTickersAdmin,
  fetchTpeResumable,
  fetchTpeStatus,
  listConfigs,
  resumeTpe,
  scheduleAllConfigs,
  setAsgDesired,
  startAllConfigs,
  startTpe,
  stopRun,
  stopTpe,
  type AwsCostResponse,
  type ConfigPromotionStats,
  type KickoffQueueSummary,
  type ResumableRun,
  type TickerAdminRow,
  type TpeActiveRun,
  type TpeFeatureConfig,
  type TpeInstance,
  type TpeAsgActivity,
} from "../api/client";
import { getCurrentUserEmail } from "../auth/cognito";

/** Admin-only TPE control panel. Email allow-list is enforced server-side
 *  by /admin/* endpoints; this component also gates the UI so non-admins
 *  see a "Not authorized" message rather than empty errors. */

const TPE_ADMIN_EMAILS = new Set(["nick@kitmitto.com"]);

function fmtTime(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Short date label for the config dropdown — M/D/YY keeps the option
// scannable even when both phase tags are present.
function fmtRunDate(iso: string | null | undefined) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "numeric", day: "numeric", year: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtDuration(isoStart: string | null | undefined) {
  if (!isoStart) return "—";
  try {
    const start = new Date(isoStart).getTime();
    const mins = Math.max(0, Math.floor((Date.now() - start) / 60000));
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  } catch {
    return "—";
  }
}

function StatPill({ label, value, color = "#cbd5e1" }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div style={{ background: "#0b1220", padding: "10px 14px", borderRadius: 6, border: "1px solid #1e293b", minWidth: 100 }}>
      <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function fmtUsdShort(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function AwsCostCard({
  data,
  error,
}: {
  data: AwsCostResponse | undefined;
  error: unknown;
}) {
  if (error) {
    return (
      <div className="card" style={{ color: "#fca5a5" }}>
        AWS cost unavailable: {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }
  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <h4 style={{ margin: 0, color: "#93c5fd" }}>AWS account cost</h4>
        <div style={{ color: "#64748b", fontSize: 11 }}>
          {data?.as_of
            ? `Cost Explorer · UTC days · refreshed ${new Date(data.as_of).toLocaleTimeString()}`
            : "Cost Explorer · UTC days"}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <StatPill label="MTD" value={fmtUsdShort(data?.mtd)} color="#fbbf24" />
        <StatPill label="WTD" value={fmtUsdShort(data?.wtd)} color="#fbbf24" />
        <StatPill label="Previous day" value={fmtUsdShort(data?.previous_day)} color="#cbd5e1" />
        <StatPill label="Current day" value={fmtUsdShort(data?.current_day)} color="#cbd5e1" />
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
        Account-wide unblended cost. Cost Explorer data lags real time by up to 24h, so
        the current-day total is typically partial.
      </div>
    </div>
  );
}

// Relative "time until" for a future ISO timestamp — used for the
// scheduled-batch countdown. Goes negative-safe ("now / firing") once the
// fire time passes but the queue hasn't transitioned yet.
function fmtUntil(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
    if (mins <= 0) return "now / firing";
    if (mins < 60) return `in ${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `in ${h}h ${m}m`;
  } catch {
    return "—";
  }
}

const QUEUE_STATUS_COLORS: Record<string, string> = {
  pending: "#fbbf24",
  running: "#3b82f6",
  done: "#22c55e",
  succeeded: "#22c55e",
  failed: "#ef4444",
  cancelled: "#94a3b8",
};

function QueueStatusBadges({ counts }: { counts: Record<string, number> }) {
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length === 0) return <span style={{ color: "#64748b" }}>—</span>;
  return (
    <>
      {entries.map(([s, n]) => (
        <span
          key={s}
          style={{
            background: QUEUE_STATUS_COLORS[s] ?? "#374151",
            color: "#0b1220", padding: "1px 6px", borderRadius: 3,
            fontSize: 10, fontWeight: 700, marginRight: 4, whiteSpace: "nowrap",
          }}
        >{n} {s}</span>
      ))}
    </>
  );
}

function ScheduledBatchesCard({
  queues,
  busy,
  onCancel,
}: {
  queues: KickoffQueueSummary[];
  busy: boolean;
  onCancel: (queueId: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (queues.length === 0) {
    return (
      <div className="card" style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13 }}>
        No TPE batches. Use “Schedule all configs…” to queue an overnight run, or “Run P1 for all configs”.
      </div>
    );
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>TPE batches ({queues.length})</h3>
      <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>
        Scheduled, run-all, and manual batches — all gated by the global concurrency cap.
        <strong> Cancel</strong> stops the whole batch: pending configs are cancelled and
        already-running configs are aborted.
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Queue</th>
              <th>Fires</th>
              <th className="num">Configs</th>
              <th>Status</th>
              <th>Scheduled by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {queues.map((q) => {
              const pending = q.status_counts["pending"] ?? 0;
              const running = q.status_counts["running"] ?? 0;
              const stoppable = pending + running > 0;
              const isExpanded = expanded === q.queue_id;
              return (
                <Fragment key={q.queue_id}>
                  <tr>
                    <td><code style={{ fontSize: 11 }}>{q.queue_id}</code></td>
                    <td style={{ fontSize: 12 }}>
                      {fmtTime(q.fire_at)}
                      {pending > 0 && (
                        <div style={{ color: "#fbbf24", fontSize: 11 }}>{fmtUntil(q.fire_at)}</div>
                      )}
                    </td>
                    <td className="num">{q.total}</td>
                    <td><QueueStatusBadges counts={q.status_counts} /></td>
                    <td style={{ fontSize: 11, color: "#94a3b8" }}>{q.scheduled_by || "—"}</td>
                    <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      <button
                        onClick={() => setExpanded(isExpanded ? null : q.queue_id)}
                        style={{
                          background: "#0b1220", border: "1px solid #334155", color: "#cbd5e1",
                          borderRadius: 4, fontSize: 11, padding: "4px 10px", cursor: "pointer",
                          marginRight: 6,
                        }}
                      >{isExpanded ? "Hide" : "Details"}</button>
                      {stoppable && (
                        <button
                          onClick={() => onCancel(q.queue_id)}
                          disabled={busy}
                          title="Stop the whole batch: cancel pending configs and abort running ones"
                          style={{
                            background: busy ? "#334155" : "#7f1d1d", color: "#fff",
                            border: "1px solid #dc2626", borderRadius: 4, fontSize: 11,
                            padding: "4px 10px", cursor: busy ? "not-allowed" : "pointer",
                          }}
                        >Stop batch</button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} style={{ background: "#0b1220", padding: 0 }}>
                        <table style={{ width: "100%" }}>
                          <thead>
                            <tr>
                              <th style={{ width: 50 }}>#</th>
                              <th>Config</th>
                              <th>Status</th>
                              <th>Run</th>
                            </tr>
                          </thead>
                          <tbody>
                            {q.items.map((it) => (
                              <tr key={it.position}>
                                <td>{it.position}</td>
                                <td><code style={{ fontSize: 11, color: "#e0e7ff" }}>{it.config_name}</code></td>
                                <td>
                                  <span style={{
                                    background: QUEUE_STATUS_COLORS[it.status] ?? "#374151",
                                    color: "#0b1220", padding: "1px 6px", borderRadius: 3,
                                    fontSize: 10, fontWeight: 700,
                                  }}>{it.status}</span>
                                  {it.error_msg && (
                                    <div style={{ color: "#fca5a5", fontSize: 10, marginTop: 2 }}>{it.error_msg}</div>
                                  )}
                                </td>
                                <td style={{ fontSize: 11, color: "#94a3b8" }}>
                                  {it.run_id ? <code>{it.run_id}</code> : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActiveRunsTable({
  runs,
  selectedArn,
  onSelect,
  onStopRun,
  busy,
  sharedTotal,
}: {
  runs: TpeActiveRun[];
  selectedArn: string | null;
  onSelect: (arn: string | null) => void;
  onStopRun: (executionArn: string, label: string) => void;
  busy: boolean;
  sharedTotal: { instances: number; hourly: number; tasks: number };
}) {
  if (runs.length === 0) {
    return (
      <div className="card" style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13 }}>
        No TPE runs currently active.
      </div>
    );
  }
  const kindLabel = (k: TpeActiveRun["kind"]) =>
    k === "phase1_only" ? "Phase 1" : k === "resume" ? "Resume" : "Full TPE";
  const kindColor = (k: TpeActiveRun["kind"]) =>
    k === "phase1_only" ? "#0284c7" : k === "resume" ? "#7c3aed" : "#16a34a";

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <h4 style={{ margin: 0, color: "#93c5fd" }}>
          Active runs ({runs.length})
        </h4>
        <div style={{ color: "#64748b", fontSize: 11 }}>
          Shared cluster: {sharedTotal.instances} EC2 · ${sharedTotal.hourly.toFixed(2)}/hr · {sharedTotal.tasks} running tasks
          {selectedArn && (
            <>
              {" · "}
              <button
                onClick={() => onSelect(null)}
                style={{
                  background: "none", border: "1px solid #334155", color: "#93c5fd",
                  borderRadius: 4, fontSize: 11, padding: "2px 8px", cursor: "pointer",
                }}
              >Clear filter</button>
            </>
          )}
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Kind</th>
              <th>Run ID</th>
              <th>Config</th>
              <th>Scope</th>
              <th>State</th>
              <th>Round</th>
              <th>Running</th>
              <th>Pending</th>
              <th>Succeeded</th>
              <th>Failed</th>
              <th>EC2 (shared)</th>
              <th>$/hr</th>
              <th>Started</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const isSelected = selectedArn === r.execution_arn;
              return (
                <tr
                  key={r.execution_arn}
                  onClick={() => onSelect(isSelected ? null : r.execution_arn)}
                  style={{
                    cursor: "pointer",
                    background: isSelected ? "#1e1b4b" : undefined,
                  }}
                  title={isSelected ? "Click to clear filter" : "Click to filter dashboard to this run"}
                >
                  <td>
                    <span style={{
                      background: kindColor(r.kind), color: "#fff",
                      padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                    }}>{kindLabel(r.kind)}</span>
                  </td>
                  <td><code style={{ fontSize: 11 }}>{r.run_id || r.name}</code></td>
                  <td style={{ fontSize: 11 }}>
                    {r.feature_config_label ? (
                      <code style={{ color: "#e0e7ff" }}>{r.feature_config_label}</code>
                    ) : (
                      <span style={{ color: "#64748b", fontStyle: "italic" }}>default</span>
                    )}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {/* Scope = which ticker(s) this run is for. Targeted
                        Phase 2 runs (started from RunCompare's "Start
                        Phase 2 for {ticker}") show the single ticker
                        prominently; normal multi-ticker runs show the
                        count with the full list on hover. */}
                    {r.restrict_to_ticker ? (
                      <div title={`Targeted Phase 2 for a single ticker: ${r.restrict_to_ticker}`}>
                        <code style={{ color: "#E69F00", fontWeight: 700, fontSize: 12 }}>
                          ★ {r.restrict_to_ticker}
                        </code>
                        <div style={{ color: "#64748b", fontSize: 9 }}>targeted</div>
                      </div>
                    ) : r.tickers && r.tickers.length > 0 ? (
                      <span
                        title={r.tickers.join(", ")}
                        style={{ color: "#cbd5e1" }}
                      >
                        {r.tickers.length} ticker{r.tickers.length === 1 ? "" : "s"}
                      </span>
                    ) : (
                      <span style={{ color: "#475569" }}>—</span>
                    )}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {r.current_state || "—"}
                    {r.current_child_state && (
                      <div style={{ color: "#64748b", fontSize: 10 }}>→ {r.current_child_state}</div>
                    )}
                  </td>
                  <td style={{ fontSize: 12 }}>{r.round_label}</td>
                  <td style={{ color: "#3b82f6", fontWeight: 600 }}>{r.tasks_running}</td>
                  <td style={{ color: "#eab308" }}>{r.tasks_pending}</td>
                  <td style={{ color: "#22c55e" }}>{r.tasks_succeeded}</td>
                  <td style={{ color: r.tasks_failed > 0 ? "#ef4444" : "#cbd5e1" }}>{r.tasks_failed}</td>
                  <td style={{ color: "#cbd5e1" }}>{r.ec2_instances_shared}</td>
                  <td style={{ color: "#cbd5e1" }}>${r.hourly_cost_usd.toFixed(2)}</td>
                  <td style={{ fontSize: 11, color: "#94a3b8" }}>{fmtDuration(r.start_date)}</td>
                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                    {r.run_id && (
                      <Link
                        to={`/runs/${encodeURIComponent(r.run_id)}`}
                        style={{ color: "#93c5fd", fontSize: 11 }}
                      >Detail →</Link>
                    )}
                    <button
                      onClick={() => onStopRun(r.execution_arn, r.run_id || r.name)}
                      disabled={busy}
                      title="Stop just this run (the next queued run takes its slot)"
                      style={{
                        marginLeft: 8, background: "#7f1d1d", color: "#fff",
                        border: "1px solid #dc2626", padding: "2px 8px",
                        borderRadius: 4, fontSize: 11, fontWeight: 600,
                        cursor: busy ? "not-allowed" : "pointer",
                      }}
                    >Stop</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatePill({ status }: { status: string | undefined }) {
  const colors: Record<string, [string, string]> = {
    RUNNING: ["#1e40af", "#93c5fd"],
    SUCCEEDED: ["#14532d", "#86efac"],
    FAILED: ["#7f1d1d", "#fca5a5"],
    TIMED_OUT: ["#7c2d12", "#fdba74"],
    ABORTED: ["#4c1d95", "#c4b5fd"],
  };
  const [bg, fg] = colors[(status || "").toUpperCase()] ?? ["#374151", "#d1d5db"];
  return (
    <span style={{ background: bg, color: fg, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600 }}>
      {status || "—"}
    </span>
  );
}

function fmtUsd(v: number | null | undefined, digits = 4): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `$${Number(v).toFixed(digits)}`;
}

function InstancesTable({ instances }: { instances: TpeInstance[] }) {
  if (instances.length === 0) {
    return <div style={{ color: "#94a3b8", fontStyle: "italic" }}>No instances running.</div>;
  }
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Instance ID</th>
            <th>Type</th>
            <th>Market</th>
            <th>State</th>
            <th>AZ</th>
            <th>Launched</th>
            <th className="num">Uptime</th>
            <th className="num">Tasks</th>
            <th className="num">Rate / hr</th>
            <th className="num">Cost since launch</th>
          </tr>
        </thead>
        <tbody>
          {instances.map((i) => (
            <tr key={i.instance_id}>
              <td><code style={{ fontSize: 11 }}>{i.instance_id}</code></td>
              <td>{i.instance_type}</td>
              <td>
                <span style={{
                  padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 600,
                  background: i.market_type === "spot" ? "#1e3a8a" : "#374151",
                  color: i.market_type === "spot" ? "#93c5fd" : "#d1d5db",
                }}>
                  {i.market_type}
                </span>
              </td>
              <td>{i.state}</td>
              <td>{i.az ?? "—"}</td>
              <td style={{ fontSize: 12 }}>{fmtTime(i.launch_time)}</td>
              <td className="num">{fmtDuration(i.launch_time)}</td>
              <td className="num" title={i.pending_tasks ? `${i.pending_tasks} pending` : undefined}>
                {i.running_tasks ?? "—"}
                {i.pending_tasks ? <span style={{ color: "#94a3b8", fontSize: 11 }}> (+{i.pending_tasks})</span> : null}
              </td>
              <td className="num" title={i.rate_source}>{fmtUsd(i.hourly_rate_usd, 4)}</td>
              <td className="num" style={{ fontWeight: 600 }}>{fmtUsd(i.cost_usd, 2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityTable({ activity }: { activity: TpeAsgActivity[] }) {
  if (activity.length === 0) {
    return <div style={{ color: "#94a3b8", fontStyle: "italic" }}>No recent ASG activity.</div>;
  }
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Time</th>
            <th>Description</th>
            <th>Cause</th>
            <th>Status</th>
            <th className="num">%</th>
          </tr>
        </thead>
        <tbody>
          {activity.map((a) => (
            <tr key={a.activity_id}>
              <td style={{ fontSize: 11 }}>{fmtTime(a.start_time)}</td>
              <td style={{ fontSize: 12 }}>{a.description}</td>
              <td style={{ fontSize: 11, color: "#94a3b8" }}>{a.cause}</td>
              <td><StatePill status={a.status_code} /></td>
              <td className="num">{a.progress ?? 0}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AsgSizingControls({
  currentDesired,
  disabled,
  onChanged,
}: {
  currentDesired: number;
  disabled: boolean;
  onChanged: () => void;
}) {
  // Local "target" buffer (string!) so the user can type a number without
  // each keystroke firing an API call, AND without the controlled
  // type="number" input fighting them on the leading zero (iOS in
  // particular wouldn't let you replace the "0" — you'd get "05", "010").
  // We parse to a number only when applying.
  const [target, setTarget] = useState<string>(String(currentDesired));
  const [pending, setPending] = useState(false);
  useEffect(() => { setTarget(String(currentDesired)); }, [currentDesired]);

  const targetNum = Math.max(0, parseInt(target, 10) || 0);

  async function apply(value: number) {
    if (value < 0 || !Number.isFinite(value)) return;
    if (value === currentDesired) return;
    setPending(true);
    try {
      const res = await setAsgDesired(value);
      window.alert(`ASG desired set to ${res.desired}\n` +
                   `Min/Max now ${res.min}/${res.max}`);
      onChanged();
    } catch (e) {
      window.alert(`Set failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPending(false);
    }
  }

  const btnStyle = (variant: "ghost" | "primary" | "danger") => ({
    padding: "6px 12px",
    border: "1px solid #334155",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled || pending ? "not-allowed" : "pointer",
    background:
      variant === "primary" ? "#2563eb"
        : variant === "danger" ? "#7f1d1d"
        : "#0b1220",
    color:
      variant === "ghost" ? "#cbd5e1" : "#fff",
  } as const);

  return (
    <div style={{
      background: "#0b1220",
      border: "1px solid #1e293b",
      borderRadius: 6,
      padding: 12,
      display: "flex",
      gap: 8,
      alignItems: "center",
      flexWrap: "wrap",
    }}>
      <span style={{ color: "#94a3b8", fontSize: 12, marginRight: 4 }}>Set desired:</span>
      <button onClick={() => apply(currentDesired - 1)}
              disabled={disabled || pending || currentDesired <= 0}
              style={btnStyle("ghost")} title="-1">−1</button>
      <button onClick={() => apply(currentDesired + 1)}
              disabled={disabled || pending}
              style={btnStyle("ghost")} title="+1">+1</button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={target}
        disabled={disabled || pending}
        onChange={(e) => {
          const v = e.target.value;
          // Allow empty (so user can clear and retype) or digits only.
          if (v === "" || /^\d+$/.test(v)) setTarget(v);
        }}
        onBlur={() => {
          // Normalize on blur: empty → 0, strip leading zeros.
          setTarget(String(targetNum));
        }}
        style={{
          width: 70, padding: "6px 8px",
          background: "#0f172a", color: "#e2e8f0",
          border: "1px solid #334155", borderRadius: 4,
        }}
      />
      <button onClick={() => apply(targetNum)}
              disabled={disabled || pending || targetNum === currentDesired}
              style={btnStyle("primary")}>
        {pending ? "Setting…" : "Apply"}
      </button>
      <button onClick={() => apply(0)}
              disabled={disabled || pending || currentDesired === 0}
              style={btnStyle("danger")}
              title="Scale ASG to 0 (instances drain naturally)">
        Scale to 0
      </button>
      <span style={{ color: "#64748b", fontSize: 11, marginLeft: "auto" }}>
        Current: {currentDesired}
      </span>
    </div>
  );
}

export function TpeAdminPage() {
  const [email, setEmail] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    getCurrentUserEmail().then(setEmail);
  }, []);

  const isAdmin = typeof email === "string" && TPE_ADMIN_EMAILS.has(email.toLowerCase());

  // Run picker. null = default (most recent running or latest terminal);
  // a string ARN = scope all panels below to that specific run.
  const [selectedExecArn, setSelectedExecArn] = useState<string | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["tpe-status", selectedExecArn],
    queryFn: () => fetchTpeStatus(selectedExecArn),
    refetchInterval: 30_000,  // poll every 30s while page is open
    enabled: isAdmin,
  });

  // Active runs polled more frequently — when something's running, we want
  // the table to refresh quickly so the user sees state transitions.
  const { data: activeRuns } = useQuery({
    queryKey: ["tpe-active-runs"],
    queryFn: fetchActiveRuns,
    refetchInterval: 15_000,
    enabled: isAdmin,
  });

  // Auto-clear selection if the chosen run has finished (would otherwise
  // get stuck showing stale terminal data).
  useEffect(() => {
    if (!selectedExecArn || !activeRuns) return;
    const stillActive = activeRuns.runs.some((r) => r.execution_arn === selectedExecArn);
    if (!stillActive) setSelectedExecArn(null);
  }, [selectedExecArn, activeRuns]);

  const { data: resumable } = useQuery({
    queryKey: ["tpe-resumable"],
    queryFn: fetchTpeResumable,
    refetchInterval: 60_000,
    enabled: isAdmin,
  });

  // Scheduled batches (fire-later sequential queues). Polled so a pending
  // batch's countdown + per-config status stay fresh, and so a cancel
  // reflects quickly.
  const { data: kickoffQueue } = useQuery({
    queryKey: ["kickoff-queue"],
    queryFn: fetchKickoffQueue,
    refetchInterval: 30_000,
    enabled: isAdmin,
  });

  // Cost Explorer is cached server-side for 1h ($0.01 per CE call), so
  // polling here matches that — a 15-min refetch is plenty.
  const { data: awsCost, error: awsCostError } = useQuery({
    queryKey: ["aws-cost"],
    queryFn: fetchAwsCost,
    refetchInterval: 15 * 60_000,
    enabled: isAdmin,
  });

  const { data: configsList } = useQuery({
    queryKey: ["configs"],
    queryFn: listConfigs,
    enabled: isAdmin,
    staleTime: 30_000,
  });
  const [selectedConfig, setSelectedConfig] = useState<string>("");
  // Resume picker modal: open when the user clicks the Resume button.
  // Closed automatically once a run is picked + confirmed.
  const [resumePickerOpen, setResumePickerOpen] = useState(false);
  // Combined "Start a new run" modal: contains the config picker AND the
  // Full TPE / Phase 1 only buttons. Replaces the old layout where the
  // config card sat permanently on the page; gives a single focused
  // dialog so iOS Safari can't suppress it and the main page stays clean.
  const [startRunModalOpen, setStartRunModalOpen] = useState(false);
  // Schedule-all-configs modal: sequential overnight kickoff for every
  // saved config, delayed N hours from now (1..24) so the batch hits
  // the deeper Spot pool in the US night window.
  const [scheduleAllModalOpen, setScheduleAllModalOpen] = useState(false);
  // Run-P1-for-subset modal: same as "Run P1 for all configs" but scoped
  // to a multi-select of TradingTickers symbols. Useful when adding a new
  // ticker or rerunning a name that flipped after a backtest fix, without
  // burning compute on the whole portfolio.
  const [runSubsetModalOpen, setRunSubsetModalOpen] = useState(false);

  // Promotion stats — flags "never promoted" / "never run" configs in
  // the run-config picker so the user can avoid burning compute on a
  // config that's already failed the retirement-rule test. Lazy: only
  // fetched once the start-run modal is open, since the underlying
  // endpoint scans TradingModels + TpeRunHistory.
  const { data: promotionStats } = useQuery({
    queryKey: ["config-promotion-stats"],
    queryFn: fetchConfigPromotionStats,
    enabled: isAdmin && startRunModalOpen,
    staleTime: 60_000,
  });
  const promotionByConfig: Record<string, ConfigPromotionStats> = (() => {
    const out: Record<string, ConfigPromotionStats> = {};
    for (const s of promotionStats?.configs ?? []) out[s.config_name] = s;
    return out;
  })();

  if (email === undefined) return <div>Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="card" style={{ color: "#fca5a5" }}>
        Not authorized.
        <div style={{ marginTop: 8 }}>
          <Link to="/" className="backlink">← Leaderboard</Link>
        </div>
      </div>
    );
  }

  async function runStart(phase1Only: boolean) {
    setBusy(true);
    try {
      const res = await startTpe({
        phase1Only,
        featureConfigName: selectedConfig || undefined,
      });
      const label = phase1Only ? "Phase 1 only" : "Full TPE";
      const cfg = selectedConfig || "(default)";
      if (res.started) {
        window.alert(`${label} started.\nrun_id: ${res.run_id}\nConfig: ${cfg}`);
      } else {
        window.alert(
          `${label} queued (Config: ${cfg}).\n\n` +
          `The concurrency cap (${res.cap ?? "?"}) is full — this run will start ` +
          `automatically when a slot frees. Watch the Scheduled batches / Active runs panel.`,
        );
      }
      qc.invalidateQueries({ queryKey: ["tpe-status"] });
      qc.invalidateQueries({ queryKey: ["tpe-resumable"] });
      qc.invalidateQueries({ queryKey: ["tpe-active-runs"] });
    } catch (e) {
      window.alert(`Failed to start TPE: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  // Enqueues a PHASE 1 run for every saved config, gated by the global
  // concurrency cap — starts up to the cap now and tops back up as each
  // finishes. Cancel the whole batch from the batches panel below.
  async function handleRunAllNow() {
    const n = configsList?.configs.length ?? 0;
    if (n === 0) {
      window.alert("No saved configs to run. Create one on the Configs page first.");
      return;
    }
    const ok1 = window.confirm(
      `RUN PHASE 1 FOR ALL ${n} CONFIGS NOW\n\n` +
      `Enqueues a Phase-1-only TPE run for each config and starts them up to ` +
      `the concurrency cap, topping back up as each finishes. Heavy + costly, ` +
      `but you can cancel the whole batch from the batches panel.\n\nContinue?`,
    );
    if (!ok1) return;
    const typed = window.prompt('Type "RUN ALL" to confirm:');
    if (typed !== "RUN ALL") {
      window.alert("Cancelled (did not type RUN ALL).");
      return;
    }
    setBusy(true);
    try {
      const res = await startAllConfigs(true);
      window.alert(
        `Queued ${res.queued} Phase-1 config(s); started ${res.started_now} now ` +
        `(cap ${res.cap}). The rest start automatically as runs finish.` +
        (res.errors.length > 0
          ? `\n\n${res.errors.length} failed to enqueue:\n  • ` +
            res.errors.map((e) => `${e.config_name}: ${e.error}`).join("\n  • ")
          : ""),
      );
      qc.invalidateQueries({ queryKey: ["tpe-active-runs"] });
      qc.invalidateQueries({ queryKey: ["tpe-status"] });
    } catch (e) {
      window.alert(`Run all failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  // Same wiring as handleRunAllNow but scoped to a user-picked subset of
  // tickers. Confirmation prompts mirror the "Run all" path so accidental
  // clicks during a session aren't a one-tap mistake.
  async function handleRunSubsetNow(tickers: string[]) {
    const n = configsList?.configs.length ?? 0;
    if (n === 0) {
      window.alert("No saved configs to run. Create one on the Configs page first.");
      return;
    }
    if (tickers.length === 0) {
      window.alert("Pick at least one ticker.");
      return;
    }
    const tickerList = tickers.join(", ");
    const ok1 = window.confirm(
      `RUN PHASE 1 FOR ${n} CONFIG(S) × ${tickers.length} TICKER(S)\n\n` +
      `Tickers: ${tickerList}\n\n` +
      `Each config will run Phase 1 only on this subset, gated by the global ` +
      `concurrency cap. Cancel the whole batch from the batches panel below.\n\nContinue?`,
    );
    if (!ok1) return;
    setBusy(true);
    try {
      const res = await startAllConfigs(true, tickers);
      window.alert(
        `Queued ${res.queued} Phase-1 config(s) on ${tickers.length} ticker(s); ` +
        `started ${res.started_now} now (cap ${res.cap}). The rest start ` +
        `automatically as runs finish.` +
        (res.errors.length > 0
          ? `\n\n${res.errors.length} failed to enqueue:\n  • ` +
            res.errors.map((e) => `${e.config_name}: ${e.error}`).join("\n  • ")
          : ""),
      );
      setRunSubsetModalOpen(false);
      qc.invalidateQueries({ queryKey: ["tpe-active-runs"] });
      qc.invalidateQueries({ queryKey: ["tpe-status"] });
      qc.invalidateQueries({ queryKey: ["kickoff-queue"] });
    } catch (e) {
      window.alert(`Run subset failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleCancelQueue(queueId: string) {
    const ok = window.confirm(
      `Cancel batch ${queueId}?\n\n` +
      `Marks pending configs cancelled AND aborts any that are already ` +
      `running (StopExecution). Deletes the schedule if it's a scheduled batch. ` +
      `Other batches and the ASG are left alone.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await cancelKickoffQueue(queueId);
      const stopped = res.stopped_executions?.length ?? 0;
      window.alert(
        `Cancelled ${res.cancelled_positions.length} config(s)` +
        (stopped > 0 ? `, aborted ${stopped} running execution(s).` : ".") +
        (res.errors && res.errors.length > 0
          ? `\n\n${res.errors.length} error(s):\n  • ${res.errors.join("\n  • ")}`
          : ""),
      );
      qc.invalidateQueries({ queryKey: ["kickoff-queue"] });
      qc.invalidateQueries({ queryKey: ["tpe-active-runs"] });
      qc.invalidateQueries({ queryKey: ["tpe-status"] });
    } catch (e) {
      window.alert(`Cancel failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleResume(run: ResumableRun) {
    // Picking the row in the modal IS the confirmation — don't gate it
    // behind a second window.confirm, which iOS Safari can suppress after
    // a flurry of native dialogs. The modal already shows the details.
    setResumePickerOpen(false);
    setBusy(true);
    try {
      const res = await resumeTpe(run.run_id);
      window.alert(
        res.started
          ? `TPE resumed.\nrun_id: ${res.run_id}\nexecution_arn: ${res.execution_arn}`
          : `Resume queued (run_id: ${run.run_id}).\n\nThe concurrency cap ` +
            `(${res.cap ?? "?"}) is full — it will start automatically when a slot frees.`,
      );
      qc.invalidateQueries({ queryKey: ["tpe-status"] });
      qc.invalidateQueries({ queryKey: ["tpe-resumable"] });
      qc.invalidateQueries({ queryKey: ["tpe-active-runs"] });
    } catch (e) {
      window.alert(`Resume failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleStop() {
    // Two prompts for emergency stop — it's destructive (terminates EC2,
    // aborts state machines, stops ECS tasks). Cheap insurance vs. an
    // accidental click.
    const ok1 = window.confirm(
      "EMERGENCY STOP\n\nThis will:\n" +
      "  • abort all running TPE state machine executions\n" +
      "  • set the backtest ASG desired capacity to 0\n" +
      "  • stop all running and pending ECS tasks in the backtest cluster\n\n" +
      "Continue?",
    );
    if (!ok1) return;
    const typed = window.prompt('Type "STOP" to confirm:');
    if (typed !== "STOP") {
      window.alert("Cancelled (did not type STOP).");
      return;
    }
    setBusy(true);
    try {
      const res = await stopTpe();
      window.alert(
        `Stop complete.\n` +
        `Executions aborted: ${res.executions_stopped}\n` +
        `ASG desired set to: ${res.asg_desired_set}\n` +
        `ECS tasks stopped: ${res.tasks_stopped}\n` +
        (res.errors.length > 0 ? `\nErrors:\n  • ${res.errors.join("\n  • ")}` : "No errors."),
      );
      qc.invalidateQueries({ queryKey: ["tpe-status"] });
    } catch (e) {
      window.alert(`Stop failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  // Stop one specific run. Leaves the ASG and pending queue alone — the gate
  // refills the freed slot with the next queued run, so a batch keeps flowing.
  // (To stop a whole batch, use its Stop button; to halt everything, the
  // emergency stop.)
  async function handleStopRun(executionArn: string, label: string) {
    const ok = window.confirm(
      `Stop run ${label}?\n\n` +
      `Aborts just this execution. The ASG and any queued configs are left ` +
      `alone — the next queued run starts in the freed slot.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      await stopRun(executionArn);
      window.alert(`Stopped run ${label}.`);
      qc.invalidateQueries({ queryKey: ["tpe-status"] });
      qc.invalidateQueries({ queryKey: ["tpe-active-runs"] });
      qc.invalidateQueries({ queryKey: ["kickoff-queue"] });
    } catch (e) {
      window.alert(`Stop run failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const sm = data?.state_machine;
  const map = data?.map_run;
  const asg = data?.asg;
  const ecs = data?.ecs;
  // The current run shown in the State machine card — driven by the
  // server-side `current_execution_arn` (set when the user picks a row
  // from the Active runs table) with a fallback to the first running exec.
  const currentArn = sm?.current_execution_arn ?? null;
  const liveExec =
    sm?.running_executions?.find((e) => e.execution_arn === currentArn) ??
    sm?.running_executions?.[0];

  return (
    <div>
      <Link to="/" className="backlink">← Leaderboard</Link>
      <h2 style={{ marginTop: 12 }}>TPE backtest control</h2>
      <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 0 }}>
        Live view of the backtest infrastructure (state machine, ASG, EC2, ECS). Polls every 30 seconds.
        {" • "}
        <Link to="/runs/history" style={{ color: "#93c5fd" }}>View run history →</Link>
        {" · "}
        <Link to="/admin/configs" style={{ color: "#93c5fd" }}>Manage configs →</Link>
      </p>

      {/* Active runs table — multiple TPE runs can be in flight at
          once (the Start buttons no longer block when one is running).
          Clicking a row scopes every panel below to that run. */}
      <ActiveRunsTable
        runs={activeRuns?.runs ?? []}
        selectedArn={selectedExecArn}
        onSelect={setSelectedExecArn}
        onStopRun={handleStopRun}
        busy={busy}
        sharedTotal={{
          instances: activeRuns?.shared?.asg_total_instances ?? 0,
          hourly: activeRuns?.shared?.asg_total_hourly_usd ?? 0,
          tasks: activeRuns?.shared?.total_running_tasks ?? 0,
        }}
      />

      {/* Control bar — start / resume / stop / refresh */}
      <div className="card" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button
          onClick={() => setStartRunModalOpen(true)}
          disabled={busy}
          title="Pick a config and start a Full TPE or Phase 1 only run"
          style={{
            background: busy ? "#334155" : "#16a34a",
            color: "#fff", border: "1px solid #15803d",
            padding: "8px 16px", borderRadius: 4,
            fontSize: 14, fontWeight: 600,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "…" : "Start a new run…"}
        </button>
        <button
          onClick={() => setScheduleAllModalOpen(true)}
          disabled={busy || !configsList || configsList.configs.length === 0}
          title="Enqueue every saved config to start N hours from now, gated by the concurrency cap (overnight Spot batch)"
          style={{
            background: busy ? "#334155" : "#0ea5e9",
            color: "#fff", border: "1px solid #0284c7",
            padding: "8px 16px", borderRadius: 4,
            fontSize: 14, fontWeight: 600,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          Schedule all configs…
        </button>
        <button
          onClick={handleRunAllNow}
          disabled={busy || !configsList || configsList.configs.length === 0}
          title="Enqueue a Phase-1 TPE run for every saved config, gated by the global concurrency cap"
          style={{
            background: busy ? "#334155" : "#ca8a04",
            color: "#fff", border: "1px solid #a16207",
            padding: "8px 16px", borderRadius: 4,
            fontSize: 14, fontWeight: 600,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "…" : `▶ Run P1 for all configs${configsList ? ` (${configsList.configs.length})` : ""}`}
        </button>
        <button
          onClick={() => setRunSubsetModalOpen(true)}
          disabled={busy || !configsList || configsList.configs.length === 0}
          title="Run P1 for every saved config, but only on a chosen subset of tickers"
          style={{
            background: busy ? "#334155" : "#b45309",
            color: "#fff", border: "1px solid #92400e",
            padding: "8px 16px", borderRadius: 4,
            fontSize: 14, fontWeight: 600,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          ▶ Run P1 for ticker subset…
        </button>
        {resumable?.resumable && resumable.runs.length > 0 && (
          <button
            onClick={() => setResumePickerOpen(true)}
            disabled={busy}
            title={
              resumable.runs.length === 1
                ? `Resume failed run ${resumable.runs[0].run_id}`
                : `${resumable.runs.length} failed runs are resumable — pick one`
            }
            style={{
              background: busy ? "#334155" : "#7c3aed",
              color: "#fff", border: "1px solid #5b21b6",
              padding: "8px 16px", borderRadius: 4,
              fontSize: 14, fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "…" : `Resume failed run${resumable.runs.length > 1 ? `s (${resumable.runs.length})` : ""}…`}
          </button>
        )}
        <button
          onClick={handleStop}
          disabled={busy}
          style={{
            background: "#7f1d1d", color: "#fff", border: "1px solid #dc2626",
            padding: "8px 16px", borderRadius: 4, fontSize: 14, fontWeight: 600,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          🛑 Emergency stop
        </button>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          style={{
            background: "#1e293b", color: "#cbd5e1", border: "1px solid #334155",
            padding: "8px 16px", borderRadius: 4, fontSize: 13, cursor: "pointer",
          }}
        >
          Refresh now
        </button>
        <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          {isFetching && <span className="spinner spinner-sm" aria-hidden="true" />}
          {isFetching
            ? (data ? "refreshing…" : "loading…")
            : (data ? `as of ${new Date().toLocaleTimeString()}` : "")}
        </span>
      </div>

      {/* Scheduled batches — fire-later sequential queues created by
          "Schedule all configs…". Shows the pending countdown + per-config
          status and offers a Cancel for anything still pending. */}
      <ScheduledBatchesCard
        queues={kickoffQueue?.queues ?? []}
        busy={busy}
        onCancel={handleCancelQueue}
      />

      {/* AWS account cost — account-wide totals from Cost Explorer.
          Cached 1h server-side; CE itself lags real time by up to 24h
          so the current_day pill is typically partial. */}
      <AwsCostCard data={awsCost} error={awsCostError} />

      {error && <div className="card" style={{ color: "#fca5a5" }}>Status error: {String(error)}</div>}

      {isLoading && !data && (
        <div className="card" style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: 14, padding: "40px 20px", color: "#94a3b8",
        }}>
          <span className="spinner spinner-md" aria-hidden="true" />
          <div style={{ fontSize: 13 }}>Loading dashboard…</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            Fetching Step Functions, ASG, EC2 and ECS state. First load can take ~5s.
          </div>
        </div>
      )}

      {/* The data panels render only once the first fetch has returned —
          otherwise the empty zeros/dashes make it look like the system is
          dead. The spinner card above conveys "we're still working". */}
      {data && <>
      {/* State machine + map run */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>State machine</h3>
        <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10, wordBreak: "break-all", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
          {(liveExec?.execution_arn ?? sm?.latest_execution?.execution_arn) ?? <em>no execution yet</em>}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <StatPill label="Running execs" value={sm?.running_count ?? 0}
                    color={(sm?.running_count ?? 0) > 0 ? "#22c55e" : "#cbd5e1"} />
          {liveExec && (
            <>
              <StatPill label="Status" value={<StatePill status={liveExec.status} />} />
              <StatPill label="Started" value={fmtTime(liveExec.start_date)} />
              <StatPill label="Uptime" value={fmtDuration(liveExec.start_date)} />
              {sm?.current_state && (
                <StatPill
                  label={sm.current_child_state ? "Current step (parent → child)" : "Current step"}
                  value={
                    <span style={{ fontSize: 14, fontFamily: "ui-monospace, monospace" }}>
                      {sm.current_state}
                      {sm.current_child_state && (
                        <>
                          <span style={{ color: "#64748b", margin: "0 4px" }}>→</span>
                          {sm.current_child_state}
                        </>
                      )}
                    </span>
                  }
                  color="#93c5fd"
                />
              )}
            </>
          )}
          {!liveExec && sm?.latest_execution && (
            <>
              <StatPill label="Latest" value={<StatePill status={sm.latest_execution.status} />} />
              <StatPill label="Started" value={fmtTime(sm.latest_execution.start_date)} />
              <StatPill label="Stopped" value={fmtTime(sm.latest_execution.stop_date)} />
            </>
          )}
        </div>

        <h4 style={{ marginBottom: 8 }}>Map run (jobs in the current/latest TPE execution)</h4>
        {map ? (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <StatPill label="Total" value={map.total} />
            <StatPill label="Pending" value={map.pending} color="#94a3b8" />
            <StatPill label="Running" value={map.running} color="#3b82f6" />
            <StatPill label="Succeeded" value={map.succeeded} color="#22c55e" />
            <StatPill label="Failed" value={map.failed} color="#ef4444" />
            <StatPill label="Failed %" value={`${map.failed_pct.toFixed(2)}%`}
                      color={map.failed_pct > 5 ? "#ef4444" : "#cbd5e1"} />
          </div>
        ) : (
          <div style={{ color: "#94a3b8", fontStyle: "italic" }}>
            No active map run. (Map run stats appear once the first child
            backtest-workflow execution reaches its Distributed Map state.)
          </div>
        )}
      </div>

      {/* ECS — scoped to the current TPE execution by filtering tasks
          whose startedBy matches one of its child backtest-workflow
          executions. */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>ECS cluster (current TPE execution)</h3>
        <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>
          {ecs?.cluster ?? <em>not found</em>}
          {typeof ecs?.child_execution_count === "number" && (
            <span style={{ marginLeft: 8 }}>
              · {ecs.child_execution_count} child execution{ecs.child_execution_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatPill label="Running tasks" value={ecs?.running ?? 0} color="#3b82f6" />
          <StatPill label="Pending tasks" value={ecs?.pending ?? 0} color="#94a3b8" />
          <StatPill label="Succeeded (recent)" value={ecs?.succeeded_recent ?? 0} color="#22c55e" />
          <StatPill label="Failed (recent)" value={ecs?.failed_recent ?? 0} color="#ef4444" />
          <StatPill label="Total stopped (recent)" value={ecs?.stopped_recent ?? 0} color="#cbd5e1" />
        </div>
        {ecs?.completion_window_note && (
          <div style={{ marginTop: 8, fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
            {ecs.completion_window_note}
          </div>
        )}
      </div>

      {/* ASG */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Auto Scaling Group</h3>
        <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>
          {asg?.name ?? <em>not found</em>}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <StatPill label="Desired" value={asg?.desired ?? 0} />
          <StatPill label="In service" value={asg?.in_service ?? 0}
                    color={(asg?.in_service ?? 0) === (asg?.desired ?? 0) ? "#22c55e" : "#fbbf24"} />
          <StatPill label="Min / Max" value={`${asg?.min ?? 0} / ${asg?.max ?? 0}`} />
          <StatPill label="EC2 instances" value={asg?.instances?.length ?? 0} />
          {asg?.cost_summary && (
            <>
              <StatPill label="Burn / hr" value={fmtUsd(asg.cost_summary.current_burn_rate_usd_per_hour, 3)}
                        color="#fbbf24" />
              <StatPill label="Burn / day"  value={fmtUsd(asg.cost_summary.current_burn_rate_usd_per_day, 2)}
                        color="#fbbf24" />
              <StatPill
                label="Cost so far"
                value={fmtUsd(asg.cost_summary.total_cost_usd, 2)}
                color="#cbd5e1"
              />
              {asg.cost_summary.terminated_instance_count > 0 && (
                <StatPill
                  label={`Terminated (${asg.cost_summary.terminated_instance_count})`}
                  value={fmtUsd(asg.cost_summary.terminated_instances_cost_usd, 2)}
                  color="#94a3b8"
                />
              )}
            </>
          )}
        </div>

        {/* Inline sizing control — increment/decrement plus an arbitrary
            "set to N" input. Goes through /admin/asg-desired which clamps
            to MinSize/MaxSize and bumps MaxSize if N exceeds it. */}
        {asg?.name && (
          <AsgSizingControls
            currentDesired={asg.desired}
            disabled={busy}
            onChanged={() => qc.invalidateQueries({ queryKey: ["tpe-status"] })}
          />
        )}

        <h4 style={{ marginTop: 16, marginBottom: 8 }}>Instances</h4>
        <InstancesTable instances={asg?.instances ?? []} />
        <h4 style={{ marginTop: 16, marginBottom: 8 }}>Recent scaling activity (last 10)</h4>
        <ActivityTable activity={asg?.activity ?? []} />
      </div>

      {/* Cost note */}
      <div className="card" style={{ background: "#0f172a", border: "1px dashed #334155", fontSize: 12, color: "#94a3b8" }}>
        <strong>Cost note:</strong> per-instance cost is computed from a hard-coded
        AWS published on-demand price table (us-east-2) multiplied by uptime hours.
        Spot rates are approximated as 30% of on-demand — actual spot pricing
        varies by instance type, AZ, and demand. For exact billed cost, consult
        AWS Cost Explorer (24-hour delay). Hover the per-hour rate to see the
        rate source.
      </div>
      </>}

      {resumePickerOpen && (
        <ResumePickerModal
          runs={resumable?.runs ?? []}
          busy={busy}
          onClose={() => setResumePickerOpen(false)}
          onPick={handleResume}
        />
      )}
      {startRunModalOpen && (
        <StartRunModal
          configs={configsList?.configs ?? []}
          selectedConfig={selectedConfig}
          onSelectConfig={setSelectedConfig}
          promotionByConfig={promotionByConfig}
          // Set of config names currently in flight — used to flag the
          // dropdown options + warn before re-starting the same config.
          runningConfigs={
            new Set(
              (activeRuns?.runs ?? [])
                .map((r) => r.feature_config_label || "")
                .filter((s) => s.length > 0),
            )
          }
          busy={busy}
          onClose={() => setStartRunModalOpen(false)}
          onStart={(phase1Only) => {
            setStartRunModalOpen(false);
            runStart(phase1Only);
          }}
        />
      )}
      {scheduleAllModalOpen && (
        <ScheduleAllConfigsModal
          configs={configsList?.configs ?? []}
          onClose={() => setScheduleAllModalOpen(false)}
        />
      )}
      {runSubsetModalOpen && (
        <RunSubsetModal
          configCount={configsList?.configs.length ?? 0}
          busy={busy}
          onClose={() => setRunSubsetModalOpen(false)}
          onRun={handleRunSubsetNow}
        />
      )}
    </div>
  );
}

// Single row inside the StartRunModal's config table. Clicking the row
// (or the radio cell) selects it. Selected rows get a tinted background,
// running configs get an amber RUNNING badge in the name cell.
function ConfigRow({
  isSelected,
  isRunning,
  onPick,
  name,
  description,
  lastUsed,
  promotion,
}: {
  isSelected: boolean;
  isRunning: boolean;
  onPick: () => void;
  name: React.ReactNode;
  description: React.ReactNode;
  lastUsed: string;
  /** Promotion-rate stats for this config (when known). Drives the
   *  "never promoted" / "never run" warning chip below the name. */
  promotion?: ConfigPromotionStats;
}) {
  // Build the warning chip data. Orange luminance + icon + text per the
  // project CVD memory — never communicate the warning by color alone.
  let chip: { glyph: string; label: string; tooltip: string } | null = null;
  if (promotion) {
    if (promotion.never_used) {
      chip = {
        glyph: "🛑",
        label: "Never run",
        tooltip: "This config has never been kicked off. Likely safe to retire if you're trimming the catalog.",
      };
    } else if (promotion.never_promoted) {
      chip = {
        glyph: "🛑",
        label: `Never promoted (${promotion.runs_total} runs)`,
        tooltip: `Ran on ${promotion.well_spaced_run_count}+ distinct days ≥3 days apart and produced 0 promotions across ${promotion.run_ticker_pairs_total} (run × ticker) trials. Strong retirement candidate — about $${Math.round(promotion.total_cost_usd).toLocaleString()} of compute already spent.`,
      };
    } else if (
      promotion.run_ticker_pairs_total >= 10 &&
      promotion.promotion_rate < 0.1
    ) {
      chip = {
        glyph: "⚠️",
        label: `Low rate (${promotion.promotions_total}/${promotion.run_ticker_pairs_total})`,
        tooltip: `Only ${(promotion.promotion_rate * 100).toFixed(1)}% of (run × ticker) trials have ever produced a promotion. Consider reviewing before re-running.`,
      };
    }
  }
  return (
    <tr
      onClick={onPick}
      style={{
        cursor: "pointer",
        background: isSelected ? "#1e1b4b" : undefined,
      }}
    >
      <td style={{ textAlign: "center", verticalAlign: "top" }}>
        <input
          type="radio"
          checked={isSelected}
          onChange={onPick}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select this config"
        />
      </td>
      <td style={{ verticalAlign: "top" }}>
        <div>{name}</div>
        {isRunning && (
          <span style={{
            display: "inline-block", marginTop: 4,
            background: "#422006", color: "#fde68a",
            border: "1px solid #ca8a04",
            padding: "1px 6px", borderRadius: 3,
            fontSize: 10, fontWeight: 600,
          }}>● RUNNING</span>
        )}
        {chip && (
          <span
            title={chip.tooltip}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              marginTop: 4, marginLeft: isRunning ? 6 : 0,
              background: "#1c1917", color: "#fed7aa",
              border: "1px solid #c2410c",
              padding: "1px 6px", borderRadius: 3,
              fontSize: 10, fontWeight: 600,
            }}
          >
            <span>{chip.glyph}</span>
            <span>{chip.label}</span>
          </span>
        )}
      </td>
      <td style={{
        verticalAlign: "top", color: "#cbd5e1", fontSize: 12,
        lineHeight: 1.5, whiteSpace: "normal",
      }}>{description}</td>
      <td style={{
        verticalAlign: "top", color: "#94a3b8", fontSize: 11,
        whiteSpace: "normal",
      }}>{lastUsed || "—"}</td>
    </tr>
  );
}

function StartRunModal({
  configs,
  selectedConfig,
  onSelectConfig,
  promotionByConfig,
  runningConfigs,
  busy,
  onClose,
  onStart,
}: {
  configs: TpeFeatureConfig[];
  selectedConfig: string;
  onSelectConfig: (name: string) => void;
  promotionByConfig: Record<string, ConfigPromotionStats>;
  runningConfigs: Set<string>;
  busy: boolean;
  onClose: () => void;
  onStart: (phase1Only: boolean) => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const picked = configs.find((c) => c.config_name === selectedConfig);
  const pinCount = picked ? Object.keys(picked.overrides ?? {}).length : 0;
  const lastP1 = picked?.last_run_phase1_at ? fmtRunDate(picked.last_run_phase1_at) : "";
  const lastFull = picked?.last_run_full_at ? fmtRunDate(picked.last_run_full_at) : "";
  const lastUsedBits: string[] = [];
  if (lastP1) lastUsedBits.push(`P1: ${lastP1}`);
  if (lastFull) lastUsedBits.push(`Full: ${lastFull}`);
  // Pre-existing runs use the saved-config name to identify themselves;
  // re-starting the same config while it's still in flight is usually
  // accidental (double-tap), so we surface it loudly.
  const selectedIsRunning = !!selectedConfig && runningConfigs.has(selectedConfig);

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Start a new TPE run"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827", border: "1px solid #1e293b", borderRadius: 8,
          width: "100%", maxWidth: 880, padding: 20,
          maxHeight: "90vh", overflow: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: "#e2e8f0" }}>Start a new TPE run</h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "1px solid #334155",
              color: "#cbd5e1", padding: "4px 10px", borderRadius: 4, cursor: "pointer",
            }}
          >Close</button>
        </div>

        {/* Config picker — selectable table, not a dropdown, so the
            descriptions can wrap freely without ellipsis. */}
        <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 4 }}>
          Config ({configs.length + 1} options)
        </label>
        <div className="table-wrap" style={{
          background: "#0b1220",
          border: "1px solid #334155",
          borderRadius: 4,
          maxHeight: 360,
          overflowY: "auto",
        }}>
          <table style={{ tableLayout: "fixed", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 40, position: "sticky", top: 0, background: "#0b1220", zIndex: 1 }}></th>
                <th style={{ width: "30%", position: "sticky", top: 0, background: "#0b1220", zIndex: 1 }}>Config</th>
                <th style={{ position: "sticky", top: 0, background: "#0b1220", zIndex: 1 }}>Description</th>
                <th style={{ width: 110, position: "sticky", top: 0, background: "#0b1220", zIndex: 1 }}>Last used</th>
              </tr>
            </thead>
            <tbody>
              {/* Default search-space row */}
              <ConfigRow
                isSelected={selectedConfig === ""}
                isRunning={false}
                onPick={() => onSelectConfig("")}
                name={<em style={{ color: "#94a3b8" }}>(default search space)</em>}
                description={<span style={{ color: "#94a3b8" }}>
                  No pins — Optuna searches the full default space.
                </span>}
                lastUsed=""
              />
              {configs.map((c) => {
                const p1 = c.last_run_phase1_at ? fmtRunDate(c.last_run_phase1_at) : "";
                const full = c.last_run_full_at ? fmtRunDate(c.last_run_full_at) : "";
                const usedBits: string[] = [];
                if (p1) usedBits.push(`P1 ${p1}`);
                if (full) usedBits.push(`Full ${full}`);
                return (
                  <ConfigRow
                    key={c.config_name}
                    isSelected={selectedConfig === c.config_name}
                    isRunning={runningConfigs.has(c.config_name)}
                    onPick={() => onSelectConfig(c.config_name)}
                    name={<code style={{ fontSize: 12, color: "#e0e7ff" }}>{c.config_name}</code>}
                    description={c.description || <span style={{ color: "#64748b" }}>—</span>}
                    lastUsed={usedBits.length > 0 ? usedBits.join(" · ") : ""}
                    promotion={promotionByConfig[c.config_name]}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ color: "#64748b", fontSize: 11, marginTop: 6 }}>
          {picked
            ? `${pinCount} pinned override${pinCount === 1 ? "" : "s"}`
              + (lastUsedBits.length > 0 ? ` · last used ${lastUsedBits.join(" · ")}` : "")
            : "Optuna will search the full default space."}
        </div>

        {selectedIsRunning && (
          <div style={{
            marginTop: 10, padding: "10px 12px",
            background: "#422006", border: "1px solid #ca8a04",
            borderRadius: 6, fontSize: 12, lineHeight: 1.5,
            color: "#fde68a",
          }}>
            ⚠️ <strong>This config is already in a running TPE execution.</strong>{" "}
            Starting another now will run a second independent execution on the
            shared ASG. If that's not what you meant, cancel and watch the Active
            runs table.
          </div>
        )}

        {/* Start buttons — each has a short why-pick-this hint */}
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            onClick={() => onStart(false)}
            disabled={busy}
            style={{
              background: busy ? "#334155" : "#16a34a",
              color: "#fff", border: "1px solid #15803d",
              padding: "10px 16px", borderRadius: 4,
              fontSize: 14, fontWeight: 600,
              cursor: busy ? "wait" : "pointer",
              textAlign: "left",
            }}
          >
            <div>{busy ? "Starting…" : "Start full TPE"}</div>
            <div style={{ fontWeight: 400, fontSize: 11, opacity: 0.9, marginTop: 2 }}>
              Phase 1 search + Phase 2 replicates (~10k+ jobs, hours to complete).
            </div>
          </button>
          <button
            onClick={() => onStart(true)}
            disabled={busy}
            style={{
              background: busy ? "#334155" : "#0284c7",
              color: "#fff", border: "1px solid #0369a1",
              padding: "10px 16px", borderRadius: 4,
              fontSize: 14, fontWeight: 600,
              cursor: busy ? "wait" : "pointer",
              textAlign: "left",
            }}
          >
            <div>{busy ? "Starting…" : "Start Phase 1 only"}</div>
            <div style={{ fontWeight: 400, fontSize: 11, opacity: 0.9, marginTop: 2 }}>
              4 TPE search rounds (~5,200 trials), skips Phase 2. Use for ablation runs.
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleAllConfigsModal({
  configs,
  onClose,
}: {
  configs: TpeFeatureConfig[];
  onClose: () => void;
}) {
  // Default = 6 hours: targets ~midnight local time when clicked in the
  // late afternoon, lining up with the deeper US-night Spot pool the
  // user is aiming for. Operator can override 1..24.
  const [hours, setHours] = useState<number>(6);
  // Default to Phase-1-only — the all-configs batch is a screening pass.
  const [phase1Only, setPhase1Only] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ queue_id: string; fire_at: string } | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Derived preview of when the first config fires. Pure UI math —
  // server is authoritative on the actual fire_at it stamps into DDB.
  const fireAt = new Date(Date.now() + hours * 3600 * 1000);
  const fireAtLocal = fireAt.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
  const fireAtUtc = fireAt.toISOString().replace("T", " ").slice(0, 16) + " UTC";

  const hoursValid = Number.isFinite(hours) && hours >= 1 && hours <= 24;

  async function handleSubmit() {
    if (!hoursValid || busy) return;
    setBusy(true);
    try {
      const res = await scheduleAllConfigs(hours, phase1Only);
      setResult({ queue_id: res.queue_id, fire_at: res.fire_at });
    } catch (e) {
      window.alert(`Schedule failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Schedule all configs"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827", border: "1px solid #1e293b", borderRadius: 8,
          width: "100%", maxWidth: 640, padding: 20, maxHeight: "90vh", overflow: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: "#e2e8f0" }}>Schedule all configs</h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "1px solid #334155",
              color: "#cbd5e1", padding: "4px 10px", borderRadius: 4, cursor: "pointer",
            }}
          >Close</button>
        </div>

        {result ? (
          <div style={{ color: "#cbd5e1", fontSize: 14, lineHeight: 1.6 }}>
            <div style={{ color: "#22c55e", fontWeight: 600, marginBottom: 8 }}>
              ✓ Scheduled
            </div>
            <div>Queue ID: <code style={{ color: "#fbbf24" }}>{result.queue_id}</code></div>
            <div>Batch fires at: <code>{result.fire_at}</code> UTC</div>
            <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
              Track progress on the Monitors page. At fire-time the configs drain under the
              concurrency cap; failures send an email via the backtest-workflow SNS topic and the
              gate continues with the next config automatically.
            </div>
            <div style={{ marginTop: 12 }}>
              <button
                onClick={onClose}
                style={{
                  background: "#0ea5e9", color: "#fff", border: "1px solid #0284c7",
                  padding: "6px 14px", borderRadius: 4, fontSize: 13, fontWeight: 600,
                  cursor: "pointer",
                }}
              >OK</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ color: "#cbd5e1", fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
              Enqueues every saved config at fire-time, then drains them under the global
              concurrency cap — several run at once and the gate tops back up as each finishes.
              Designed for overnight Spot — pick a delay that lands in the US night window.
              Cancel the whole batch anytime from the TPE batches panel.
            </div>

            <label style={{ color: "#94a3b8", fontSize: 12, display: "block", marginBottom: 4 }}>
              Hours from now (1–24)
            </label>
            <input
              type="number"
              min={1}
              max={24}
              step={1}
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              style={{
                background: "#0b1220", border: "1px solid #334155", color: "#e2e8f0",
                padding: "6px 10px", borderRadius: 4, fontSize: 14, width: 120,
              }}
            />
            {!hoursValid && (
              <div style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>
                Must be an integer between 1 and 24.
              </div>
            )}

            <div style={{ marginTop: 14, padding: 10, background: "#0b1220",
                          border: "1px solid #334155", borderRadius: 4, fontSize: 13,
                          color: "#cbd5e1" }}>
              <div><strong>First config fires at:</strong></div>
              <div style={{ marginTop: 4 }}>Local: <code>{fireAtLocal}</code></div>
              <div>UTC: <code>{fireAtUtc}</code></div>
              <div style={{ marginTop: 8 }}>
                <strong>{configs.length}</strong> config{configs.length === 1 ? "" : "s"} will be enqueued (gated by the cap):
              </div>
              <div style={{ marginTop: 4, color: "#94a3b8", fontSize: 12 }}>
                {configs.length === 0
                  ? "(no saved configs found — visit the Configs page to create one)"
                  : configs.map((c) => c.config_name).join(", ")}
              </div>
            </div>

            <label style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center",
                            color: "#cbd5e1", fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={phase1Only}
                onChange={(e) => setPhase1Only(e.target.checked)}
              />
              Phase 1 only (screening pass — no Phase 2 promotion)
            </label>

            <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={onClose}
                style={{
                  background: "transparent", border: "1px solid #334155",
                  color: "#cbd5e1", padding: "8px 16px", borderRadius: 4, cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={!hoursValid || busy || configs.length === 0}
                style={{
                  background: busy || !hoursValid || configs.length === 0 ? "#334155" : "#0ea5e9",
                  color: "#fff", border: "1px solid #0284c7",
                  padding: "8px 16px", borderRadius: 4, fontSize: 14, fontWeight: 600,
                  cursor: busy || !hoursValid || configs.length === 0 ? "not-allowed" : "pointer",
                }}
              >{busy ? "Scheduling…" : "Schedule"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Multi-select picker for "Run P1 for ticker subset…". Loads the same
 *  TradingTickers list as the admin page so the user only sees symbols
 *  the backtest pipeline will actually accept (enabled_for_backtest).
 *
 *  Submitting calls back into the page handler, which posts the chosen
 *  list to /admin/start-all-configs. The backend stamps `tickers_subset`
 *  on the SFN input so the history page can render a "Subset" badge. */
function RunSubsetModal({
  configCount,
  busy,
  onClose,
  onRun,
}: {
  configCount: number;
  busy: boolean;
  onClose: () => void;
  onRun: (tickers: string[]) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["tickers-admin"],
    queryFn: fetchTickersAdmin,
    staleTime: 30_000,
  });

  // Filter to backtest-eligible tickers — running a config against a
  // ticker not flagged enabled_for_backtest would fail at start_tpe
  // anyway (the lambda re-validates). Sort alphabetically so the user
  // sees a predictable list every time.
  const available: TickerAdminRow[] = (data?.tickers ?? [])
    .filter((t) => t.enabled_for_backtest)
    .slice()
    .sort((a, b) => a.ticker.localeCompare(b.ticker));

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");

  function toggle(ticker: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  }

  const filtered = filter.trim()
    ? available.filter((t) => t.ticker.toLowerCase().includes(filter.trim().toLowerCase()))
    : available;

  const allShownSelected = filtered.length > 0 && filtered.every((t) => selected.has(t.ticker));

  function toggleAllShown() {
    setSelected((cur) => {
      const next = new Set(cur);
      if (allShownSelected) {
        filtered.forEach((t) => next.delete(t.ticker));
      } else {
        filtered.forEach((t) => next.add(t.ticker));
      }
      return next;
    });
  }

  const selectedList = Array.from(selected).sort();
  const canSubmit = selectedList.length > 0 && configCount > 0 && !busy;

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Run P1 for ticker subset"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827", border: "1px solid #1e293b", borderRadius: 8,
          width: "100%", maxWidth: 640, padding: 20, maxHeight: "90vh", overflow: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: "#e2e8f0" }}>Run P1 for ticker subset</h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "1px solid #334155",
              color: "#cbd5e1", padding: "4px 10px", borderRadius: 4, cursor: "pointer",
            }}
          >Close</button>
        </div>

        <div style={{ color: "#cbd5e1", fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
          Enqueues a Phase-1 run of <strong>every saved config</strong> on the
          tickers you pick — gated by the global concurrency cap. The run shows
          up in TPE history flagged <span style={{
            background: "#7c2d12", color: "#fed7aa", padding: "1px 6px",
            borderRadius: 3, fontSize: 11, fontWeight: 600,
          }}>SUBSET</span> so you can tell it apart from full-portfolio batches.
        </div>

        {isLoading && (
          <div style={{ color: "#94a3b8", fontSize: 13, padding: "12px 0" }}>
            Loading tickers…
          </div>
        )}
        {error && (
          <div style={{ color: "#fca5a5", fontSize: 13, padding: "12px 0" }}>
            Failed to load tickers: {String(error)}
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
              <input
                type="text"
                placeholder="Filter…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  background: "#0b1220", border: "1px solid #334155", color: "#e2e8f0",
                  padding: "6px 10px", borderRadius: 4, fontSize: 13, flex: 1,
                }}
              />
              <button
                onClick={toggleAllShown}
                disabled={filtered.length === 0}
                style={{
                  background: "transparent", border: "1px solid #334155",
                  color: "#cbd5e1", padding: "6px 10px", borderRadius: 4,
                  fontSize: 12, cursor: filtered.length === 0 ? "not-allowed" : "pointer",
                }}
              >
                {allShownSelected ? "Clear shown" : "Select shown"}
              </button>
            </div>

            <div style={{
              background: "#0b1220", border: "1px solid #334155", borderRadius: 4,
              maxHeight: 280, overflow: "auto", marginBottom: 12,
            }}>
              {filtered.length === 0 ? (
                <div style={{ padding: 16, color: "#94a3b8", fontSize: 13, textAlign: "center" }}>
                  {available.length === 0
                    ? "No backtest-enabled tickers found."
                    : "No tickers match the filter."}
                </div>
              ) : (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 4, padding: 8,
                }}>
                  {filtered.map((t) => {
                    const checked = selected.has(t.ticker);
                    return (
                      <label
                        key={t.ticker}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "5px 8px", borderRadius: 3, cursor: "pointer",
                          background: checked ? "#1e3a8a" : "transparent",
                          color: checked ? "#dbeafe" : "#cbd5e1",
                          fontSize: 13, fontWeight: checked ? 600 : 400,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(t.ticker)}
                          style={{ cursor: "pointer" }}
                        />
                        {t.ticker}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{
              padding: 10, background: "#0b1220",
              border: "1px solid #334155", borderRadius: 4, fontSize: 13,
              color: "#cbd5e1", marginBottom: 14,
            }}>
              <div>
                <strong>{selectedList.length}</strong> ticker{selectedList.length === 1 ? "" : "s"} selected
                {" × "}
                <strong>{configCount}</strong> config{configCount === 1 ? "" : "s"} = up to{" "}
                <strong>{selectedList.length * configCount}</strong> run{selectedList.length * configCount === 1 ? "" : "s"}
              </div>
              {selectedList.length > 0 && (
                <div style={{
                  marginTop: 6, color: "#94a3b8", fontSize: 12,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  wordBreak: "break-word",
                }}>
                  {selectedList.join(", ")}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={onClose}
                style={{
                  background: "transparent", border: "1px solid #334155",
                  color: "#cbd5e1", padding: "8px 16px", borderRadius: 4, cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={() => onRun(selectedList)}
                disabled={!canSubmit}
                style={{
                  background: !canSubmit ? "#334155" : "#b45309",
                  color: "#fff", border: "1px solid #92400e",
                  padding: "8px 16px", borderRadius: 4, fontSize: 14, fontWeight: 600,
                  cursor: !canSubmit ? "not-allowed" : "pointer",
                }}
              >
                {busy ? "Starting…" : `▶ Run P1 on ${selectedList.length} ticker${selectedList.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResumePickerModal({
  runs,
  busy,
  onClose,
  onPick,
}: {
  runs: ResumableRun[];
  busy: boolean;
  onClose: () => void;
  onPick: (run: ResumableRun) => void;
}) {
  // Tap the backdrop (not the modal body) to dismiss. ESC also closes.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Pick a failed run to resume"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827", border: "1px solid #1e293b", borderRadius: 8,
          width: "100%", maxWidth: 720, maxHeight: "80vh", overflow: "auto",
          padding: 20,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
          <h3 style={{ margin: 0, color: "#e2e8f0" }}>
            Resume a failed TPE run
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "1px solid #334155",
              color: "#cbd5e1", padding: "4px 10px", borderRadius: 4, cursor: "pointer",
            }}
          >Close</button>
        </div>
        {runs.length === 0 ? (
          <div style={{ color: "#94a3b8", fontStyle: "italic", padding: "12px 0" }}>
            No resumable runs right now.
          </div>
        ) : (
          <>
            <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>
              {runs.length} eligible run{runs.length > 1 ? "s" : ""}. Pick the one to resume —
              each uses its own existing checkpoint, so independent configs don't
              affect each other.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {runs.map((r) => {
                const kindLabel = r.resume_kind === "phase2"
                  ? `from Phase 2 (skips all ${r.num_rounds} Phase 1 rounds)`
                  : `from Phase 1 round ${r.resume_from_round}/${r.num_rounds}`;
                return (
                  <div
                    key={r.failed_execution_arn}
                    style={{
                      background: "#0b1220", border: "1px solid #1e293b",
                      borderRadius: 6, padding: "10px 12px",
                      display: "flex", gap: 12, alignItems: "center",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                        <code style={{ color: "#e0e7ff", fontSize: 13 }}>{r.run_id}</code>
                        {r.phase1_only && (
                          <span style={{
                            background: "#0284c7", color: "#fff",
                            padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 600,
                          }}>Phase 1</span>
                        )}
                        {r.feature_config_label ? (
                          <span style={{ color: "#cbd5e1", fontSize: 12 }}>
                            config: <code style={{ color: "#e0e7ff" }}>{r.feature_config_label}</code>
                          </span>
                        ) : (
                          <span style={{ color: "#64748b", fontSize: 12, fontStyle: "italic" }}>
                            default search space
                          </span>
                        )}
                      </div>
                      <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 4 }}>
                        Resumes {kindLabel}
                      </div>
                      <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>
                        Failed {r.failed_at ? new Date(r.failed_at).toLocaleString() : "—"}
                        {" · "}{r.tickers.length} ticker{r.tickers.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <button
                      onClick={() => onPick(r)}
                      disabled={busy}
                      style={{
                        background: busy ? "#334155" : "#7c3aed",
                        color: "#fff", border: "1px solid #5b21b6",
                        padding: "6px 14px", borderRadius: 4,
                        fontSize: 13, fontWeight: 600,
                        cursor: busy ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >Resume</button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
